/**
 * Owners Meeting Page Enhancement Module
 * Adds a simplified voting section to the owners meeting invitation page.
 */
const OwnersMeetingModule = (() => {
  // Private state
  const state = {
    currentMeetingId: null,
    domainId: null,
    initialized: false,
    observer: null,
    debug: true, // Set to true for verbose logging
  }

  // Constants
  const SELECTORS = {
    scrollElement: '#scrollElement',
    sectionContent: '.section-content',
    formSection: '.FormSectionDisabled',
    sectionNumber: '.section-number',
    sectionTitle: '.section-title',
  }

  const URL_REGEX = /^\/owners-meeting\/protocol\/edit\/([0-9]+)$/

  /**
   * Debug logger
   */
  const log = (...args) => {
    if (state.debug) {
      console.log('[Lux Inject - Owners Meeting Voting]', ...args)
    }
  }

  /**
   * Get domainId from JWT token in local storage
   */
  const getDomainId = () => {
    try {
      const authData = localStorage.getItem('auth')
      if (!authData) return null
      const { token } = JSON.parse(authData)
      if (!token) return null
      const decodedToken = JSON.parse(atob(token.split('.')[1]))
      return decodedToken.domainId
    } catch (error) {
      log('Error getting domainId:', error)
      return null
    }
  }

  /**
   * Cleanup function
   */
  const cleanup = () => {
    log('Cleaning up module')
    if (state.observer) {
      state.observer.disconnect()
      state.observer = null
    }
    $('#OwnersMeetingAnwesenheitSection .section-header').off('click')
    $(SELECTORS.scrollElement).off('click', '.section-header')
    $('.FormSidebar').off('click', '.Link.main-link')
    // Use attr instead of data for better compatibility
    $(SELECTORS.scrollElement).attr('data-voting-listener-added', null)
    $('.FormSidebar').attr('data-sidebar-listener-added', null)
    // Remove sidebar item
    $('#sidebar-voting-item').remove()
    state.initialized = false
  }

  /**
   * Initialize the module
   */
  const init = () => {
    log('Initializing module')
    
    // Check if already initialized and section exists
    if (state.initialized && $('#OwnersMeetingAnwesenheitSection').length > 0) {
      log('Module already initialized and section exists, skipping')
      return
    }
    
    cleanup()

    const path = window.location.pathname
    const match = path.match(URL_REGEX)
    if (!match) {
      log('URL does not match pattern, not initializing.')
      return
    }
    const meetingIdFromUrl = match[1]
    state.domainId = getDomainId()

    // Function to check if page is ready
    const isPageReady = () => {
      const $scrollElement = $(SELECTORS.scrollElement)
      if (!$scrollElement.length) return false
      
      const $lastChild = $scrollElement.children().last()
      // Page is ready if there's content and it's not the loading state
      return $lastChild.length && !$lastChild.hasClass('FormSectionDisabled')
    }

    // Check if page is already ready (for fast-loading pages)
    if (isPageReady()) {
      log('Page is already ready, enhancing immediately')
      enhancePage(meetingIdFromUrl)
      return
    }

    // Page not ready yet, set up MutationObserver
    log('Page not ready yet, setting up MutationObserver')
    
    const observerCallback = (mutations, obs) => {
      if (isPageReady()) {
        log('Page is ready for enhancement (via MutationObserver)')
        obs.disconnect()
        state.observer = null
        enhancePage(meetingIdFromUrl)
      }
    }

    state.observer = new MutationObserver(observerCallback)
    state.observer.observe(document.body, { childList: true, subtree: true })
    log('MutationObserver is watching for page changes.')
    
    // Also check periodically in case mutations don't trigger
    const checkInterval = setInterval(() => {
      if (isPageReady()) {
        log('Page is ready for enhancement (via interval check)')
        clearInterval(checkInterval)
        if (state.observer) {
          state.observer.disconnect()
          state.observer = null
        }
        enhancePage(meetingIdFromUrl)
      }
    }, 500) // Check every 500ms
    
    // Clear interval after 10 seconds to prevent infinite checking
    setTimeout(() => {
      clearInterval(checkInterval)
      log('Stopped interval checking after timeout')
    }, 10000)
  }

  /**
   * Enhance the page with the voting section
   */
  const enhancePage = (meetingId) => {
    try {
      // Check if the section already exists
      if ($('#OwnersMeetingAnwesenheitSection').length > 0) {
        log('Voting section already exists, skipping creation')
        state.initialized = true
        return
      }
      
      state.currentMeetingId = meetingId
      const $newSection = createAnwesenheitSection()
      if (!$newSection) throw new Error('Failed to create the Anwesenheit section.')
      
      $(SELECTORS.scrollElement).append($newSection)
      
      // Add sidebar item
      addSidebarItem()

      // Add click handler for section toggle
      $newSection.find('.section-header').first().on('click', handleSectionClick)
      
      // Add click handler for the voting button
      $newSection.on('click', '#go-to-voting-btn', () => {
        if (state.currentMeetingId && state.domainId) {
          window.open(`/voting/${state.currentMeetingId}?domainId=${state.domainId}`, '_blank')
        } else {
          log('Missing meetingId or domainId')
        }
      })
      
      // Add event delegation to detect when other sections are clicked
      // First check if we haven't already added this listener
      if ($(SELECTORS.scrollElement).attr('data-voting-listener-added') !== 'true') {
        $(SELECTORS.scrollElement).on('click', '.section-header', function(e) {
          console.log({asf:2})
          const $clickedHeader = $(this)
          const $clickedSection = $clickedHeader.closest('.FormSection')
          
          // Only handle clicks on other sections (not our voting section)
          if (!$clickedSection.is('#OwnersMeetingAnwesenheitSection')) {
            // Check if the clicked section is opening (doesn't have open class yet)
            const isOpening = !$clickedHeader.hasClass('open')
            
            if (isOpening) {
              // Close our voting section when another section opens
              closeVotingSection()
              // Update sidebar to remove active state
              updateSidebarState(false)
            }
          }
        })
        $(SELECTORS.scrollElement).attr('data-voting-listener-added', 'true')
      }
      
      // Add event delegation for sidebar clicks on other sections
      if ($('.FormSidebar').attr('data-sidebar-listener-added') !== 'true') {
        $('.FormSidebar').on('click', '.Link.main-link', function(e) {
          const $clickedLink = $(this)
          const placeholder = e.target.parentNode;
          placeholder.classList.add('active')
          
          // If it's not our voting sidebar item
          if (!$clickedLink.is('#sidebar-voting-item')) {
            // Use a small delay to let the other section open first
            setTimeout(() => {
              // Close our voting section
              closeVotingSection()
            }, 50)
          }
        })
        $('.FormSidebar').attr('data-sidebar-listener-added', 'true')
      }
      
      state.initialized = true
      log('Page enhancement complete')
    } catch (error) {
      console.error('[Lux Inject] Enhancement failed:', error)
    }
  }

  /**
   * Create the Anwesenheit section element
   */
  const createAnwesenheitSection = () => {
    const $scrollElement = $(SELECTORS.scrollElement)
    if (!$scrollElement.length) {
      log('Error: scrollElement not found.')
      return null
    }
    
    const $lastSectionNumber = $scrollElement.find(SELECTORS.sectionNumber).last()
    const nextSectionNumber = ($lastSectionNumber.length) ? (parseInt($lastSectionNumber.text(), 10) || 0) + 1 : 1

    const sectionHtml = `
      <div class="FormSection" id="OwnersMeetingAnwesenheitSection">
        <div class="form-section-wrapper">
          <div class="main-section-header section-header">
            <div class="section-number-box">
              <div class="section-number">${nextSectionNumber}</div>
            </div>
            <div class="section-title-box">
              <div class="section-title">Abstimmung</div>
            </div>
          </div>
          <div class="section-content" style="display: none;">
            <div class="sub-section last" id="votingSubSection">
              <div class="sub-section-header">
                <div class="sub-section-title">Eigentümerversammlung starten</div>
              </div>
              <div class="sub-section-content">
                <div class="sub-section-content-row">
                  <div class="sub-section-content-column" style="min-width: 25rem;">
                    <button id="go-to-voting-btn" type="button" class="ant-btn ant-btn-default Button  AdditionalEntityButton add-agenda-item">
                      <span>Eigentümerversammlung starten</span>
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    `
    
    log('Created voting section with number', nextSectionNumber)
    return $(sectionHtml)
  }

  /**
   * Close all other sections except the voting section
   */
  const closeOtherSections = () => {
    $(SELECTORS.scrollElement).find('.FormSection').each(function() {
      const $section = $(this)
      
      // Skip our voting section
      if ($section.is('#OwnersMeetingAnwesenheitSection')) {
        return
      }
      
      // Close this section if it's open
      const $wrapper = $section.find('.form-section-wrapper')
      const $header = $section.find('.section-header').first()
      const $content = $section.find('.section-content').first()
      
      if ($wrapper.hasClass('open')) {
        $wrapper.removeClass('open')
        $header.removeClass('open')
        $header.find('.section-number-box, .section-title-box').removeClass('open')
        $content.hide()
        log('Closed section:', $section.attr('id') || 'unnamed')
      }
    })
  }

  /**
   * Close the voting section
   */
  const closeVotingSection = () => {
    const $section = $('#OwnersMeetingAnwesenheitSection')
    if (!$section.length) return
    
    const $wrapper = $section.find('.form-section-wrapper')
    const $header = $section.find('.section-header').first()
    const $content = $section.find('.section-content').first()
    
    if ($wrapper.hasClass('open')) {
      $wrapper.removeClass('open')
      $header.removeClass('open')
      $header.find('.section-number-box, .section-title-box').removeClass('open')
      $content.hide()
      
      // Update sidebar state
      updateSidebarState(false)
      
      log('Closed voting section')
    }
  }

  /**
   * Add voting section to sidebar
   */
  const addSidebarItem = () => {
    const $sidebar = $('.FormSidebar')
    if (!$sidebar.length) {
      log('Sidebar not found')
      return
    }
    
    // Check if sidebar item already exists
    if ($('#sidebar-voting-item').length > 0) {
      log('Sidebar item already exists')
      return
    }
    
    // Get the section number from the created section
    const sectionNumber = $('#OwnersMeetingAnwesenheitSection .section-number').text()
    
    // Create sidebar item HTML
    const sidebarItemHtml = `
      <div class="Link main-link" id="sidebar-voting-item">
        <div class="link-row level1">
          <div class="arrow-wrapper" role="button" tabindex="-1">
            &nbsp;&nbsp;&nbsp;&nbsp;
          </div>
          <div class="link-title" role="button" tabindex="-1">
            ${sectionNumber}&nbsp;&nbsp;&nbsp;&nbsp;Abstimmung
          </div>
        </div>
      </div>
    `
    
    // Append to sidebar
    $sidebar.append(sidebarItemHtml)
    
    // Add click handler to sidebar item
    $('#sidebar-voting-item').on('click', function(e) {
      e.stopPropagation()
      
      const $section = $('#OwnersMeetingAnwesenheitSection')
      const $header = $section.find('.section-header').first()
      
      // Toggle the section by simulating a click on its header
      $header.trigger('click')
    })
    
    log('Added voting section to sidebar')
  }
  
  /**
   * Update sidebar state (active/inactive)
   */
  const updateSidebarState = (isOpen) => {
    const $sidebarItem = $('#sidebar-voting-item .link-row')
    
    if (!$sidebarItem.length) return
    
    if (isOpen) {
      // Remove active from other sidebar items
      $('.FormSidebar .link-row.active').removeClass('active')
      // Add active to our item
      $sidebarItem.addClass('active')
      log('Updated sidebar: active')
    } else {
      $sidebarItem.removeClass('active')
      log('Updated sidebar: inactive')
    }
  }

  /**
   * Handle section click event
   */
  const handleSectionClick = function (e) {
    e.stopPropagation() // Prevent event from bubbling up
    
    const $header = $(this)
    const $sectionWrapper = $header.closest('.form-section-wrapper')
    const $content = $sectionWrapper.find('.section-content')
    
    const isCurrentlyOpen = $header.hasClass('open')
    
    if (!isCurrentlyOpen) {
      // Opening the section - close all other sections first
      closeOtherSections()
      
      // Open this section
      $header.addClass('open')
      $header.find('.section-number-box, .section-title-box').addClass('open')
      $sectionWrapper.addClass('open')
      $content.show()
      
      // Update sidebar state
      updateSidebarState(true)
      
      log('Opened voting section')
    } else {
      // Closing the section
      $header.removeClass('open')
      $header.find('.section-number-box, .section-title-box').removeClass('open')
      $sectionWrapper.removeClass('open')
      $content.hide()
      
      // Update sidebar state
      updateSidebarState(false)
      
      log('Closed voting section')
    }
  }


  // Public API
  return {
    init,
    cleanup,
  }
})()

// Main entry point
const onOwnersMeetingPageVoting = () => {
  console.log('Lux Inject: Running onOwnersMeetingPageVoting')
  OwnersMeetingModule.init()
}

// Cleanup on page unload
window.addEventListener('beforeunload', () => {
  OwnersMeetingModule.cleanup()
})
